import React from "react";
import { ConfigProvider } from "antd";
import type { Locale } from "antd/es/locale";
import arEG from "antd/locale/ar_EG";
import bgBG from "antd/locale/bg_BG";
import csCZ from "antd/locale/cs_CZ";
import daDK from "antd/locale/da_DK";
import deDE from "antd/locale/de_DE";
import elGR from "antd/locale/el_GR";
import enUS from "antd/locale/en_US";
import esES from "antd/locale/es_ES";
import etEE from "antd/locale/et_EE";
import fiFI from "antd/locale/fi_FI";
import frFR from "antd/locale/fr_FR";
import gaIE from "antd/locale/ga_IE";
import hrHR from "antd/locale/hr_HR";
import huHU from "antd/locale/hu_HU";
import itIT from "antd/locale/it_IT";
import ltLT from "antd/locale/lt_LT";
import lvLV from "antd/locale/lv_LV";
import nlNL from "antd/locale/nl_NL";
import plPL from "antd/locale/pl_PL";
import ptPT from "antd/locale/pt_PT";
import roRO from "antd/locale/ro_RO";
import ruRU from "antd/locale/ru_RU";
import skSK from "antd/locale/sk_SK";
import slSI from "antd/locale/sl_SI";
import svSE from "antd/locale/sv_SE";
import ukUA from "antd/locale/uk_UA";
import zhCN from "antd/locale/zh_CN";
import { useTranslation } from "react-i18next";
import { isRtlLanguage, resolveAppLanguage, type AppLanguage } from "../i18n";
import { getUiDensity, setUiDensity, type UiDensity } from "../compliance/ux/uiPrefs";

/** Ant Design has no Maltese pack — fall back to en_US. `zh` uses zh_CN (Simplified). */
const ANTD_LOCALES: Record<AppLanguage, Locale> = {
  ar: arEG,
  bg: bgBG,
  cs: csCZ,
  da: daDK,
  de: deDE,
  el: elGR,
  en: enUS,
  es: esES,
  et: etEE,
  fi: fiFI,
  fr: frFR,
  ga: gaIE,
  hr: hrHR,
  hu: huHU,
  it: itIT,
  lt: ltLT,
  lv: lvLV,
  mt: enUS,
  nl: nlNL,
  pl: plPL,
  pt: ptPT,
  ro: roRO,
  ru: ruRU,
  sk: skSK,
  sl: slSI,
  sv: svSE,
  uk: ukUA,
  zh: zhCN,
};

type Props = {
  children: React.ReactNode;
};

type DensityContextValue = {
  density: UiDensity;
  setDensity: (d: UiDensity) => void;
};

export const UiDensityContext = React.createContext<DensityContextValue>({
  density: "default",
  setDensity: () => undefined,
});

export function AntdLocaleProvider({ children }: Props) {
  const { i18n } = useTranslation();
  const lang = resolveAppLanguage(i18n.language);
  const [density, setDensityState] = React.useState<UiDensity>(() => getUiDensity());

  const setDensity = React.useCallback((d: UiDensity) => {
    setUiDensity(d);
    setDensityState(d);
  }, []);

  return (
    <UiDensityContext.Provider value={{ density, setDensity }}>
      <ConfigProvider
        locale={ANTD_LOCALES[lang]}
        direction={isRtlLanguage(lang) ? "rtl" : "ltr"}
        componentSize={density === "compact" ? "small" : "middle"}
        theme={{
          token: {
            // Landing / EUDoRa primary blue (EUDORA-preview-landing.html --blue)
            colorPrimary: "#0b6cff",
            colorLink: "#0b6cff",
            colorInfo: "#0b6cff",
          },
        }}
      >
        {children}
      </ConfigProvider>
    </UiDensityContext.Provider>
  );
}
